import React, {useContext, useEffect, useState} from 'react';
import {useHistory, useParams} from "react-router-dom";
import {FormattedMessage, injectIntl} from "react-intl";

// Material UI
import {Grid} from "@mui/material";

// Components
import RightDrawer from "components/WMS/Mobile/RightDrawer";
import ToggleNavigation from "components/WMS/Mobile/Toggle/ToggleNavigation";
import Toggle from "components/WMS/Mobile/Toggle/Toggle";
import ClickableChip from "components/WMS/ClickableChip";
import AddRetailers from "./components/AddRetailers";
import CustomButton from "components/CustomButton";
import BottomActionContainer from "components/WMS/Mobile/BottomActionContainer";

// Local components
import RetailerItemsDrawer from "./components/RetailerItemsDrawer";
import NoValidateProducts from "./components/NoValidateProducts";
import RetailerList from "./components/RetailerList";
import RefreshPopup from "./components/Popups/RefreshPopup";
import InTrolleyToast from "./components/Toasts/InTrolleyToast";
import EndPickingRetailerToast from "./components/Toasts/EndPickingRetailerToast";
import TaskHeaderRightContent from "./components/TaskHeaderRightContent";
import EndPickingMissionPopup from "./components/Popups/EndPickingMissionPopup";
import RefreshButton from "./components/RefreshButton";
import ZoneContainer from "./components/ZoneContainer";
import ProductNotFoundToast from "./components/Toasts/ProductNotFoundToast";
import ProductAlreadyInTrolleyToast from "./components/Toasts/ProductAlreadyInTrolleyToast";

// Context
import {BarCodeScannerContext} from "contexts/barCodeScannerContext";
import {MissionContext} from "./context/MissionContext";
import {WMSContext} from "../context/WMSContext";

// API
import {getWmsItems, wmsItemInTrolley} from "api/wms/wmsItems/wmsItems";
import {getTask} from "api/wms/tasks/list";
import {getWMSContainerWithCodeBar} from "api/wms/wmsContainers/wmsContainers";
import {endMission} from "api/wms/tasks/end";
import {addRetailerItemsToTask} from "api/wms/tasks/addRetailerItemsToTask";

// Utils
import {WmsItemsStatus} from "utils/WmsItemsStatus";
import {WmsTaskStatus} from "utils/WMSTaskStatus";
import {WMSRoutes} from "utils/WMSRoutes";

function Task(props) {
    const [openRetailerItemList, setOpenRetailerItemList] = useState(false);
    const [openAddRetailers, setOpenAddRetailers] = useState(false);

    const [openScannedItemToast, setOpenScannedItemToast] = useState(false);
    const [scannedItemMulti, setScannedItemMulti] = useState(false)
    const [openEndPickingRetailerToast, setOpenEndPickingRetailerToast] = useState({
        isOpen: false,
        lastScannedWmsItem: null
    });
    const [openProductNotFoundToast, setOpenProductNotFoundToast] = useState(false);
    const [openProductAlreadyInTrolleyToast, setOpenProductAlreadyInTrolleyToast] = useState(false);

    const [openRefreshPopup, setOpenRefreshPopup] = useState(false);
    const [openEndPickingMissionPopup, setOpenEndPickingMissionPopup] = useState(false);
    const [openCancelMissionPopup, setOpenCancelMissionPopup] = useState(false);

    const [retailers, setRetailers] = useState([]);

    const [itemsToPick, setItemsToPick] = useState(0)
    const [itemsInTrolley, setItemsInTrolley] = useState(0)
    const [itemsCancelled, setItemsCancelled] = useState(0);

    const [retailerItems, setRetailerItems] = useState([]);
    const [retailerNewWmsItems, setRetailerNewWmsItems] = useState(null);
    const [selectedRetailers, setSelectedRetailers] = useState({});
    const [retailersToBeAddedToMission, setRetailersToBeAddedToMission] = useState([]);

    const [zoneFilters, setZoneFilters] = useState({});

    const [selectedZone, setSelectedZone] = useState('all');
    const [filterValue, setFilterValue] = React.useState(0);

    const [selectedRetailer, setSelectedRetailer] = useState(null);

    const [wmsItems, setWmsItems] = useState(null);
    const [availableWmsItems, setAvailableWmsItems] = useState([]);

    const [currentContainer, setCurrentContainer] = useState(null);

    const [loading, setLoading] = useState(false)

    const {scannedString, resetScannedString, setEnableScan} = useContext(BarCodeScannerContext);
    const {currentInventory, currentMember, setCurrentTask, currentTask} = useContext(WMSContext);

    let { taskId, containerBarcode } = useParams();

    const history = useHistory();

    // did mount
    useEffect(() => {
        if (!currentInventory) {
            return;
        }

        getWMSContainerWithCodeBar(containerBarcode).then((container) => {
            setCurrentContainer(container);
        })

        getTask(taskId).then((task) => {
            if (task.status === WmsTaskStatus.COMPLETED) {
                history.push(WMSRoutes.PICKING)
            }
            setCurrentTask(task);
        })
        setEnableScan(true)
        refreshMission();
    }, [currentInventory]);

    // on scan
    useEffect(() => {
        if (!scannedString) {
            return;
        }

        setEnableScan(false)

        const matchedWmsItems = wmsItems.filter(item => item.barcode === scannedString);

        if (matchedWmsItems.length === 0) {
            setOpenProductNotFoundToast(true);
            setEnableScan(true);
            return;
        }

        const allAlreadyInTrolley = matchedWmsItems.every(item => WmsItemsStatus.IN_TROLLEY === item.status);

        if (allAlreadyInTrolley) {
            setOpenProductAlreadyInTrolleyToast(true);
            setEnableScan(true);
            return;
        }

        const item = matchedWmsItems.find(item => item.status === WmsItemsStatus.PICKING);

        if (!item) {
            setEnableScan(true)
            return;
        }

        putWmsItemInTrolley(item).then(() => {
            if (wasLastItemOfRetailer(item)) {
                setOpenEndPickingRetailerToast({
                    isOpen: true,
                    lastScannedWmsItem: item
                })
            } else {
                setOpenScannedItemToast(true);
                setScannedItemMulti(item.lineItem.currentShipment.multi)
            }

            refreshMission()
        })
    }, [scannedString]);

    // on wmsItems list refresh
    useEffect(() => {
        if (!Array.isArray(wmsItems) || !currentTask) {
            return;
        }

        setItemsCancelled(getCancelledTotal(wmsItems));

        const availableWmsItems = wmsItems;

        setAvailableWmsItems(availableWmsItems);
        setRetailers(getRetailersByWmsItem(availableWmsItems));

        setItemsToPick(getPickingTotal(availableWmsItems));
        setItemsInTrolley(getInTrolleyTotal(availableWmsItems));

        setZoneFilters(getZoneFilters(availableWmsItems));

        // when task is finished, we end the mission
        if (
            (
                wmsItems.length === 0
                || wmsItems.every((item) => item.status === WmsItemsStatus.IN_TROLLEY)
            )
            && currentTask.status === WmsTaskStatus.IN_PROGRESS
            && currentMember
        ) {
            endMission(currentTask, currentMember).then(() => {
                setOpenEndPickingMissionPopup(true);
            });
        }
        setEnableScan(true)
    }, [wmsItems, currentTask]);

    // when changing zone
    useEffect(() => {
        if (!availableWmsItems || availableWmsItems.length === 0) {
            return;
        }

        let items = []

        if ('no-place' === selectedZone) {
            items = availableWmsItems.filter((item) => 0 === item.places.length);
        } else {
            items = availableWmsItems.filter((item) => item.places.some(place => place['@id'] === selectedZone) || selectedZone === 'all');
        }

        setRetailers(getRetailersByWmsItem(items));
    }, [selectedZone]);

    // when
    useEffect(() => {
        if (retailersToBeAddedToMission.length === 0) {
            return;
        }

        if (openRefreshPopup) {
            refreshMission()
        }

        setOpenRefreshPopup(true);
    }, [retailersToBeAddedToMission])

    async function putWmsItemInTrolley(wmsItem) {
        if (!wmsItem) {
            return;
        }

        const res = await wmsItemInTrolley(wmsItem, currentContainer.codebar);

        if (res) {
            resetScannedString();
        }
    }

    function wasLastItemOfRetailer(wmsItem) {
        const retailerId = wmsItem.lineItem.retailer.id
        const pickingItems = availableWmsItems.filter(
            item => item.lineItem.retailer.id === retailerId && item.status === WmsItemsStatus.PICKING
        );

        return pickingItems.length === 1 && pickingItems[0] === wmsItem;
    }

    async function refreshMission() {
        if (retailersToBeAddedToMission.length > 0) {
            await addRetailerItemsToTask(taskId, currentInventory.id, retailersToBeAddedToMission);

            setRetailersToBeAddedToMission([]);
            setSelectedRetailer([]);
            setRetailerNewWmsItems(null); // refresh the list when opening the add retailer drawer again
        }

        getWmsItems(`inventoryId=${currentInventory.id}&wmsTask.id=${taskId}&pagination=false`).then((response) => {
            setOpenRefreshPopup(false);
            setLoading(false)
            setWmsItems(response)
        });
    }

    function getRetailersByWmsItem(wmsItems) {
        if (!wmsItems || wmsItems.length === 0) {
            return [];
        }

        let retailersByWmsItems = []

        wmsItems.forEach((item) => {
            if (!retailersByWmsItems.some(retailer => retailer.id === item.lineItem.retailer.id)) {
                retailersByWmsItems.push(item.lineItem.retailer)
            }
        })

        return retailersByWmsItems;
    }

    function openRetailerProducts(retailer) {
        setSelectedRetailer(retailer);
        setOpenRetailerItemList(true);
    }

    function finishMission() {
        if (!currentTask || !currentMember) {
            return;
        }

        if (itemsToPick > 0) {
            setOpenCancelMissionPopup(true);
        } else {
            endMission(currentTask, currentMember).then(() => {
                setOpenEndPickingMissionPopup(true);
            })
        }
    }

    function getPickingTotal(items) {
        return items.reduce((acc, item) => acc + ((item.status === WmsItemsStatus.PICKING) ? 1 : 0), 0);
    }

    function getInTrolleyTotal(items) {
        return items.reduce((acc, item) => acc + ((item.status === WmsItemsStatus.IN_TROLLEY) ? 1 : 0), 0);
    }

    function getCancelledTotal(items) {
        return items.reduce((acc, item) => acc + ((item.status === WmsItemsStatus.CANCELED) ? 1 : 0), 0);
    }

    function getInTrolleyItems(items) {
        return items.filter(item => item.status === WmsItemsStatus.IN_TROLLEY);
    }

    function getPickingItems(items) {
        return items.filter(item => item.status === WmsItemsStatus.PICKING);
    }

    function getWmsItemsByRetailer(retailer) {
        return wmsItems.filter(item => (item.lineItem.retailer.id === retailer.id) || (retailer.id === 'noRetailer' && !item.lineItem.retailer));
    }

    function getZoneFilters(wmsItems) {
        if (!wmsItems?.length) {
            return {};
        }

        const filterZones = {
            [WmsItemsStatus.PICKING]: { 'all': { name: props.intl.formatMessage({id: 'wms.myPickingMissions.filter.all'}), count: 0 } },
            [WmsItemsStatus.IN_TROLLEY]: { 'all': { name: props.intl.formatMessage({id: 'wms.myPickingMissions.filter.all'}), count: 0 } }
        };

        const updateZones = (items, status) => {
            items.forEach(item => {
                if (0 === item.places.length) {
                    let zone = filterZones[status]['no-place'];

                    if (!zone) {
                        zone = {name: 'wms.myPickingMission.toggle.noZone', count: 0};

                        filterZones[status]['no-place'] = zone;
                    }

                    zone.count += 1;
                }
                item.places.forEach(place => {
                    let zone = filterZones[status][place['@id']];

                    if (!zone) {
                        zone = {name: place.name, count: 0};
                        filterZones[status][place['@id']] = zone;
                    }

                    zone.count += 1;
                });
                filterZones[status]['all'].count += 1;
            });
        };

        updateZones(getPickingItems(wmsItems), WmsItemsStatus.PICKING);
        updateZones(getInTrolleyItems(wmsItems), WmsItemsStatus.IN_TROLLEY);

        return filterZones;
    }

    return (
        <React.Fragment>
            <MissionContext.Provider value={{
                refreshMission,
                getPickingTotal,
                getInTrolleyTotal,
                getCancelledTotal,
                currentContainer,
                setOpenCancelMissionPopup,
                retailerNewWmsItems,
                setRetailerNewWmsItems,
                selectedRetailers,
                setSelectedRetailers,
                setRetailersToBeAddedToMission,
                retailersToBeAddedToMission,
                retailers,
                getWmsItemsByRetailer,
                loading,
                setLoading
            }}>
                <RightDrawer
                    open={true}
                    handleClose={() => setOpenCancelMissionPopup(true)}
                    headerCenterContent={(<b>{currentTask?.name || <FormattedMessage id={'wms.myPickingMissions.mission.title'} />}</b>)}
                    headerRightContent={(
                        <TaskHeaderRightContent
                            setOpenCancelMissionPopup={setOpenCancelMissionPopup}
                            openCancellationPopup={openCancelMissionPopup}
                            itemsToPick={itemsToPick}
                        />
                    )}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        height: '100%',
                    }}>
                        <div style={{background: '#fff'}}>
                            <div
                                style={{
                                    padding: 16,
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <ToggleNavigation
                                    value={filterValue}
                                    onChange={(index) => setFilterValue(index)}
                                >
                                    <Toggle count={itemsToPick}>
                                        <FormattedMessage id={'wms.myPickingMissions.navigation.retrieve'} />
                                    </Toggle>
                                    <Toggle count={itemsInTrolley + itemsCancelled}>
                                        <FormattedMessage id={'wms.myPickingMissions.navigation.validated'} />
                                    </Toggle>
                                </ToggleNavigation>
                                <RefreshButton onClick={() => setOpenRefreshPopup(true)} />
                            </div>
                            <ZoneContainer>
                                {Object.keys(zoneFilters).length > 0 && Object.entries(zoneFilters[filterValue === 0 ? WmsItemsStatus.PICKING : WmsItemsStatus.IN_TROLLEY]).map(([placeId, place]) => (
                                    <ClickableChip
                                        key={place.name}
                                        count={place.count}
                                        checked={selectedZone === placeId}
                                        thick={true}
                                        noWrapText={true}
                                        onClick={() => {
                                            setSelectedZone(placeId);
                                            if (placeId === 'all') {
                                                setRetailerItems(wmsItems);
                                            } else {
                                                if ('no-place' === placeId) {
                                                    setRetailerItems(wmsItems.filter(item => 0 === item.places.length));
                                                } else {
                                                    setRetailerItems(wmsItems.filter(item => item.places.some(p => p['@id'] === placeId)));
                                                }
                                            }
                                        }}
                                    >
                                        {placeId === 'no-place' ? <FormattedMessage id={place.name}/> : place.name}
                                    </ClickableChip>
                                ))}
                            </ZoneContainer>

                            {itemsInTrolley === 0 && itemsCancelled === 0 && filterValue === 1 && (
                                <NoValidateProducts />
                            )}

                            <RetailerList
                                onClickRetailerLine={(retailer) => openRetailerProducts(retailer)}
                                retailers={retailers}
                                filterValue={filterValue}
                            />
                        </div>
                        <BottomActionContainer>
                            <Grid item xs>
                                <CustomButton
                                    fullWidth={true}
                                    thicker={true}
                                    onClick={() => finishMission()}
                                >
                                    <FormattedMessage id='wms.myPickingMissions.mission.finish' />
                                </CustomButton>
                            </Grid>
                            <Grid item>
                                <CustomButton
                                    onClick={() => setOpenAddRetailers(true)}
                                    thicker={true}
                                    color={'default'}
                                    sx={{
                                        width: '28px',
                                        padding: 0,
                                        minWidth: '48px'
                                    }}
                                >
                                    +
                                </CustomButton>
                            </Grid>
                        </BottomActionContainer>
                    </div>
                    {selectedRetailer !== null && (
                        <RetailerItemsDrawer
                            selectedFilter={filterValue}
                            open={openRetailerItemList}
                            handleClose={() => setOpenRetailerItemList(false)}
                            selectedRetailer={selectedRetailer}
                            wmsItems={wmsItems}
                        />
                    )}
                    <AddRetailers
                        open={openAddRetailers}
                        handleClose={() => setOpenAddRetailers(false)}
                    />
                    <ProductNotFoundToast
                        open={openProductNotFoundToast}
                        handleClose={() => setOpenProductNotFoundToast(false)}
                    />
                    <ProductAlreadyInTrolleyToast
                        open={openProductAlreadyInTrolleyToast}
                        handleClose={() => setOpenProductAlreadyInTrolleyToast(false)}
                    />
                </RightDrawer>
                <RefreshPopup
                    open={openRefreshPopup}
                    handleClose={() => setOpenRefreshPopup(false)}
                    onClick={refreshMission}
                />
                <EndPickingMissionPopup open={openEndPickingMissionPopup} />
                <InTrolleyToast
                    isOpen={openScannedItemToast}
                    isMulti={scannedItemMulti}
                    handleClose={() => setOpenScannedItemToast(false)}
                />
                <EndPickingRetailerToast
                    wmsItem={openEndPickingRetailerToast.lastScannedWmsItem}
                    isOpen={openEndPickingRetailerToast.isOpen}
                    handleClose={() => setOpenEndPickingRetailerToast({
                        isOpen: false,
                        lastScannedWmsItem: null
                    })}
                />
            </MissionContext.Provider>
        </React.Fragment>
    );
}

export default injectIntl(Task);
